import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, CircleUser as UserCircle, Briefcase } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { PasswordRequirements } from '../components/PasswordRequirements';
import { useAuth } from '../context/AuthContext';
import { validateEmail, validatePassword, validateName, validateUKPhone } from '../utils/validation';
import { getRegistrationErrorMessage } from '../utils/errorMessages';
import { ROUTES } from '../constants';
import { RegisterServiceUserFormData } from '../types';
import { trackCompleteRegistration } from '../utils/analytics';

type UserType = 'service_user' | 'provider' | null;

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const { registerUser } = useAuth();
  const [userType, setUserType] = useState<UserType>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fieldValidity, setFieldValidity] = useState<Record<string, boolean>>({});

  const [serviceUserForm, setServiceUserForm] = useState<RegisterServiceUserFormData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    role: 'user',
  });

  const [providerForm, setProviderForm] = useState<RegisterServiceUserFormData>({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    role: 'provider',
  });

  const handleFieldBlur = (field: string, value: string, formType: 'service_user' | 'provider') => {
    const fieldKey = `${formType}_${field}`;
    let isValid = false;

    switch (field) {
      case 'name':
        const nameValidation = validateName(value);
        isValid = nameValidation.isValid;
        if (!isValid && value.trim()) {
          setErrors({ ...errors, [field]: nameValidation.message || 'Invalid name' });
        }
        break;
      case 'email':
        isValid = validateEmail(value);
        if (!isValid && value.trim()) {
          setErrors({ ...errors, [field]: 'Please enter a valid email' });
        }
        break;
      case 'phone':
        isValid = validateUKPhone(value);
        if (!isValid && value.trim()) {
          setErrors({ ...errors, [field]: 'Please enter a valid UK phone number' });
        }
        break;
      case 'password':
        const passwordValidation = validatePassword(value);
        isValid = passwordValidation.isValid;
        if (!isValid && value) {
          setErrors({ ...errors, [field]: 'Password does not meet requirements' });
        }
        break;
      case 'confirmPassword':
        const currentPassword = formType === 'service_user' ? serviceUserForm.password : providerForm.password;
        isValid = value === currentPassword && value.length > 0;
        if (!isValid && value) {
          setErrors({ ...errors, [field]: 'Passwords do not match' });
        }
        break;
    }

    setFieldValidity({ ...fieldValidity, [fieldKey]: isValid });
  };

  const validateServiceUserForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameValidation = validateName(serviceUserForm.name);
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.message || 'Name is required';
    }

    if (!validateEmail(serviceUserForm.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!validateUKPhone(serviceUserForm.phone)) {
      newErrors.phone = 'Please enter a valid UK phone number';
    }

    const passwordValidation = validatePassword(serviceUserForm.password);
    if (!passwordValidation.isValid) {
      newErrors.password = 'Password does not meet requirements';
    }

    if (serviceUserForm.password !== serviceUserForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!serviceUserForm.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateProviderForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const nameValidation = validateName(providerForm.name);
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.message || 'Name is required';
    }

    if (!validateEmail(providerForm.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!validateUKPhone(providerForm.phone)) {
      newErrors.phone = 'Please enter a valid UK phone number';
    }

    const passwordValidation = validatePassword(providerForm.password);
    if (!passwordValidation.isValid) {
      newErrors.password = 'Password does not meet requirements';
    }

    if (providerForm.password !== providerForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!providerForm.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleServiceUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateServiceUserForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    const result = await registerUser(serviceUserForm);

    setLoading(false);

    if (result.success) {
      trackCompleteRegistration({
        content_name: 'Service User Registration',
      });
      navigate(ROUTES.VERIFY_OTP, {
        state: {
          email: serviceUserForm.email,
          password: serviceUserForm.password,
          name: serviceUserForm.name
        }
      });
    } else {
      setErrors({ submit: getRegistrationErrorMessage(result.error) });
    }
  };

  const handleProviderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProviderForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    const result = await registerUser(providerForm);

    setLoading(false);

    if (result.success) {
      trackCompleteRegistration({
        content_name: 'Care Provider Registration',
      });
      navigate(ROUTES.VERIFY_OTP, {
        state: {
          email: providerForm.email,
          password: providerForm.password,
          name: providerForm.name
        }
      });
    } else {
      setErrors({ submit: getRegistrationErrorMessage(result.error) });
    }
  };

  // Initial user type selection
  if (!userType) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-3 sm:p-4">
        <Card className="w-full max-w-2xl">
          <div className="text-center mb-6 sm:mb-8">
            <Logo size="lg" className="justify-center mb-4 sm:mb-6" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Create Your Account</h1>
            <p className="text-sm sm:text-base text-gray-600">Choose how you'd like to join NursingCareConnect</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            <button
              onClick={() => setUserType('service_user')}
              className="p-6 sm:p-8 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                  <UserCircle className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">I Need Care</h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  Find and connect with care providers for yourself or a loved one
                </p>
              </div>
            </button>

            <button
              onClick={() => setUserType('provider')}
              className="p-6 sm:p-8 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all group text-left"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                  <Briefcase className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">CQC Regulated Care Providers</h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  Join as a CQC registered care provider to connect with families
                </p>
              </div>
            </button>
          </div>

          <p className="mt-6 sm:mt-8 text-center text-sm sm:text-base text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => navigate(ROUTES.LOGIN)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Log in
            </button>
          </p>
        </Card>
      </div>
    );
  }

  // Service User Registration Form
  if (userType === 'service_user') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-3 sm:p-4 py-6 sm:py-4">
        <Card className="w-full max-w-md">
          <button
            onClick={() => setUserType(null)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 p-1 -ml-1"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            <span className="text-sm sm:text-base">Back</span>
          </button>

          <div className="text-center mb-6 sm:mb-8">
            <Logo size="md" className="justify-center mb-3 sm:mb-4" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Create Your Account
            </h1>
            <p className="text-sm sm:text-base text-gray-600">Find the perfect care for your loved ones</p>
          </div>

          {errors.submit && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleServiceUserSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Account Type
              </label>
              <input
                type="text"
                value="Service User"
                disabled
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed text-sm sm:text-base"
              />
              <p className="mt-1.5 text-xs text-gray-500">This determines your access level</p>
            </div>

            <Input
              label="Full Name"
              type="text"
              placeholder="John Smith"
              value={serviceUserForm.name}
              onChange={(e) => {
                setServiceUserForm({ ...serviceUserForm, name: e.target.value });
                if (errors.name) {
                  const nameValidation = validateName(e.target.value);
                  if (nameValidation.isValid) {
                    const newErrors = { ...errors };
                    delete newErrors.name;
                    setErrors(newErrors);
                  }
                }
              }}
              onBlur={(e) => handleFieldBlur('name', e.target.value, 'service_user')}
              error={errors.name}
              success={fieldValidity['service_user_name'] && !errors.name}
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="john@example.com"
              value={serviceUserForm.email}
              onChange={(e) => {
                setServiceUserForm({ ...serviceUserForm, email: e.target.value });
                if (errors.email && validateEmail(e.target.value)) {
                  const newErrors = { ...errors };
                  delete newErrors.email;
                  setErrors(newErrors);
                }
              }}
              onBlur={(e) => handleFieldBlur('email', e.target.value, 'service_user')}
              error={errors.email}
              success={fieldValidity['service_user_email'] && !errors.email}
            />

            <Input
              label="Phone Number"
              type="tel"
              placeholder="07700 900000"
              value={serviceUserForm.phone}
              onChange={(e) => {
                setServiceUserForm({ ...serviceUserForm, phone: e.target.value });
                if (errors.phone && validateUKPhone(e.target.value)) {
                  const newErrors = { ...errors };
                  delete newErrors.phone;
                  setErrors(newErrors);
                }
              }}
              onBlur={(e) => handleFieldBlur('phone', e.target.value, 'service_user')}
              error={errors.phone}
              success={fieldValidity['service_user_phone'] && !errors.phone}
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={serviceUserForm.password}
                onChange={(e) => {
                  setServiceUserForm({ ...serviceUserForm, password: e.target.value });
                  if (errors.password && validatePassword(e.target.value).isValid) {
                    const newErrors = { ...errors };
                    delete newErrors.password;
                    setErrors(newErrors);
                  }
                }}
                onBlur={(e) => handleFieldBlur('password', e.target.value, 'service_user')}
                error={errors.password}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            {serviceUserForm.password && (
              <div className="space-y-3">
                <PasswordRequirements requirements={validatePassword(serviceUserForm.password).requirements} />
                <div className="space-y-1">
                  <div className="flex gap-1">
                    {['weak', 'medium', 'strong'].map((level, index) => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full transition-colors ${
                          validatePassword(serviceUserForm.password).strength === 'strong'
                            ? 'bg-green-500'
                            : validatePassword(serviceUserForm.password).strength === 'medium' &&
                              index < 2
                            ? 'bg-yellow-500'
                            : index === 0
                            ? 'bg-red-500'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-600">
                    Password strength:{' '}
                    <span
                      className={
                        validatePassword(serviceUserForm.password).strength === 'strong'
                          ? 'text-green-600'
                          : validatePassword(serviceUserForm.password).strength === 'medium'
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }
                    >
                      {validatePassword(serviceUserForm.password).strength}
                    </span>
                  </p>
                </div>
              </div>
            )}

            <div className="relative">
              <Input
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={serviceUserForm.confirmPassword}
                onChange={(e) => {
                  setServiceUserForm({
                    ...serviceUserForm,
                    confirmPassword: e.target.value,
                  });
                  if (errors.confirmPassword && e.target.value === serviceUserForm.password) {
                    const newErrors = { ...errors };
                    delete newErrors.confirmPassword;
                    setErrors(newErrors);
                  }
                }}
                onBlur={(e) => handleFieldBlur('confirmPassword', e.target.value, 'service_user')}
                error={errors.confirmPassword}
                success={fieldValidity['service_user_confirmPassword'] && !errors.confirmPassword}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>

            <div className="pt-2">
              <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={serviceUserForm.acceptTerms}
                  onChange={(e) =>
                    setServiceUserForm({
                      ...serviceUserForm,
                      acceptTerms: e.target.checked,
                    })
                  }
                  className="mt-0.5 sm:mt-1 w-4 h-4 sm:w-5 sm:h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-gray-600">
                  I accept the{' '}
                  <Link to={ROUTES.TERMS_OF_SERVICE} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to={ROUTES.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </Link>
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="mt-1.5 text-xs sm:text-sm text-red-600">{errors.acceptTerms}</p>
              )}
            </div>

            <Button type="submit" variant="primary" className="w-full" isLoading={loading}>
              Create Account
            </Button>
          </form>

          <p className="mt-4 sm:mt-6 text-center text-gray-600 text-xs sm:text-sm">
            Already have an account?{' '}
            <button
              onClick={() => navigate(ROUTES.LOGIN)}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Log in
            </button>
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-3 sm:p-4 py-6 sm:py-4">
      <Card className="w-full max-w-md">
        <button
          onClick={() => setUserType(null)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 p-1 -ml-1"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span className="text-sm sm:text-base">Back</span>
        </button>

        <div className="text-center mb-6 sm:mb-8">
          <Logo size="md" className="justify-center mb-3 sm:mb-4" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Create Provider Account
          </h1>
          <p className="text-sm sm:text-base text-gray-600">Join our platform and connect with families</p>
        </div>

        {errors.submit && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs sm:text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleProviderSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Account Type
            </label>
            <input
              type="text"
              value="Care Provider"
              disabled
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed text-sm sm:text-base"
            />
            <p className="mt-1.5 text-xs text-gray-500">This determines your access level</p>
          </div>

          <Input
            label="Full Name"
            type="text"
            placeholder="John Smith"
            value={providerForm.name}
            onChange={(e) => {
              setProviderForm({ ...providerForm, name: e.target.value });
              if (errors.name) {
                const nameValidation = validateName(e.target.value);
                if (nameValidation.isValid) {
                  const newErrors = { ...errors };
                  delete newErrors.name;
                  setErrors(newErrors);
                }
              }
            }}
            onBlur={(e) => handleFieldBlur('name', e.target.value, 'provider')}
            error={errors.name}
            success={fieldValidity['provider_name'] && !errors.name}
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="john@example.com"
            value={providerForm.email}
            onChange={(e) => {
              setProviderForm({ ...providerForm, email: e.target.value });
              if (errors.email && validateEmail(e.target.value)) {
                const newErrors = { ...errors };
                delete newErrors.email;
                setErrors(newErrors);
              }
            }}
            onBlur={(e) => handleFieldBlur('email', e.target.value, 'provider')}
            error={errors.email}
            success={fieldValidity['provider_email'] && !errors.email}
          />

          <Input
            label="Phone Number"
            type="tel"
            placeholder="07700 900000"
            value={providerForm.phone}
            onChange={(e) => {
              setProviderForm({ ...providerForm, phone: e.target.value });
              if (errors.phone && validateUKPhone(e.target.value)) {
                const newErrors = { ...errors };
                delete newErrors.phone;
                setErrors(newErrors);
              }
            }}
            onBlur={(e) => handleFieldBlur('phone', e.target.value, 'provider')}
            error={errors.phone}
            success={fieldValidity['provider_phone'] && !errors.phone}
          />

          <div className="relative">
            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={providerForm.password}
              onChange={(e) => {
                setProviderForm({ ...providerForm, password: e.target.value });
                if (errors.password && validatePassword(e.target.value).isValid) {
                  const newErrors = { ...errors };
                  delete newErrors.password;
                  setErrors(newErrors);
                }
              }}
              onBlur={(e) => handleFieldBlur('password', e.target.value, 'provider')}
              error={errors.password}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>

          {providerForm.password && (
            <div className="space-y-3">
              <PasswordRequirements requirements={validatePassword(providerForm.password).requirements} />
              <div className="space-y-1">
                <div className="flex gap-1">
                  {['weak', 'medium', 'strong'].map((level, index) => (
                    <div
                      key={level}
                      className={`h-1.5 flex-1 rounded-full transition-colors ${
                        validatePassword(providerForm.password).strength === 'strong'
                          ? 'bg-green-500'
                          : validatePassword(providerForm.password).strength === 'medium' &&
                            index < 2
                          ? 'bg-yellow-500'
                          : index === 0
                          ? 'bg-red-500'
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-600">
                  Password strength:{' '}
                  <span
                    className={
                      validatePassword(providerForm.password).strength === 'strong'
                        ? 'text-green-600'
                        : validatePassword(providerForm.password).strength === 'medium'
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }
                  >
                    {validatePassword(providerForm.password).strength}
                  </span>
                </p>
              </div>
            </div>
          )}

          <div className="relative">
            <Input
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your password"
              value={providerForm.confirmPassword}
              onChange={(e) => {
                setProviderForm({
                  ...providerForm,
                  confirmPassword: e.target.value,
                });
                if (errors.confirmPassword && e.target.value === providerForm.password) {
                  const newErrors = { ...errors };
                  delete newErrors.confirmPassword;
                  setErrors(newErrors);
                }
              }}
              onBlur={(e) => handleFieldBlur('confirmPassword', e.target.value, 'provider')}
              error={errors.confirmPassword}
              success={fieldValidity['provider_confirmPassword'] && !errors.confirmPassword}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-8 sm:top-9 text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          </div>

          <div className="pt-2">
            <label className="flex items-start gap-2 sm:gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={providerForm.acceptTerms}
                onChange={(e) =>
                  setProviderForm({
                    ...providerForm,
                    acceptTerms: e.target.checked,
                  })
                }
                className="mt-0.5 sm:mt-1 w-4 h-4 sm:w-5 sm:h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 flex-shrink-0"
              />
              <span className="text-xs sm:text-sm text-gray-600">
                I accept the{' '}
                <Link to={ROUTES.TERMS_OF_SERVICE} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                  Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link to={ROUTES.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                  Privacy Policy
                </Link>
              </span>
            </label>
            {errors.acceptTerms && (
              <p className="mt-1.5 text-xs sm:text-sm text-red-600">{errors.acceptTerms}</p>
            )}
          </div>

          <Button type="submit" variant="primary" className="w-full bg-green-600 hover:bg-green-700" isLoading={loading}>
            Create Account
          </Button>
        </form>

        <p className="mt-4 sm:mt-6 text-center text-gray-600 text-xs sm:text-sm">
          Already have an account?{' '}
          <button
            onClick={() => navigate(ROUTES.LOGIN)}
            className="text-green-600 hover:text-green-700 font-medium"
          >
            Log in
          </button>
        </p>
      </Card>
    </div>
  );
};
